"use client";

import { useInView } from "framer-motion";
import { useEffect, useRef, useState } from "react";

const STAT_PATTERN = /^(\d[\d,]*)\s*(\+?)$/;

function parseStatNumber(raw: string): { value: number; suffix: string } {
  const match = raw.match(STAT_PATTERN);
  if (!match) {
    return { value: 0, suffix: "" };
  }
  const value = Number.parseInt((match[1] ?? "0").replace(/,/g, ""), 10);
  const suffix = match[2] ?? "";
  return { value, suffix };
}

export function AnimatedStat({
  number,
  label,
}: {
  number: string;
  label: string;
}) {
  const { value: target, suffix } = parseStatNumber(number);
  const [displayed, setDisplayed] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const startedRef = useRef(false);

  useEffect(() => {
    if (!inView || startedRef.current) {
      return;
    }
    startedRef.current = true;

    const duration = 1200; // ms
    const startTime = performance.now();

    function tick(now: number) {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // Ease-out cubic
      const eased = 1 - (1 - progress) ** 3;
      setDisplayed(Math.round(eased * target));
      if (progress < 1) {
        requestAnimationFrame(tick);
      }
    }

    requestAnimationFrame(tick);
  }, [inView, target]);

  return (
    <div className="space-y-1 border-border border-l pl-6" ref={ref}>
      <div className="font-bold text-5xl leading-[1.2]">
        {displayed.toLocaleString()}
        {suffix}
      </div>
      <p className="text-muted-foreground text-sm">{label}</p>
    </div>
  );
}
